import { get, set, del, keys } from 'idb-keyval';

/**
 * Offline draft storage — user-scoped to prevent data leakage on shared devices.
 *
 * Keys are scoped as `draft_{userId}_{draftId}` so Student A's offline drafts
 * are invisible to Student B on the same browser. On sign-out, AuthContext
 * calls clearAllLocalDrafts() which clears all draft keys regardless of user.
 */

const PREFIX = 'draft_';

// Current user ID for scoping — set by the sync manager on auth resolution
let _currentUserId = null;

export const setOfflineUserId = (userId) => {
  _currentUserId = userId;
};

const _scopedKey = (draftId) => {
  if (_currentUserId) {
    return `${PREFIX}${_currentUserId}_${draftId}`;
  }
  // Fallback for pre-scoped keys (should not happen in normal flow)
  return `${PREFIX}${draftId}`;
};

export const saveDraftLocally = async (draftId, data) => {
  await set(_scopedKey(draftId), {
    ...data,
    localUpdatedAt: new Date().toISOString(),
    synced: false,
    userId: _currentUserId,
  });
};

export const getLocalDraft = async (draftId) => {
  // Try scoped key first
  const scoped = await get(_scopedKey(draftId));
  if (scoped) return scoped;

  // Legacy unscoped keys are not returned — they cannot be safely attributed
  // to the current user. They will be cleared on next sign-out.
  return null;
};

export const getUnsyncedDrafts = async () => {
  if (!_currentUserId) return []; // No user set — cannot safely sync anything

  const allKeys = await keys();
  const draftKeys = allKeys.filter(k => typeof k === 'string' && k.startsWith(PREFIX));
  const unsynced = [];
  for (const key of draftKeys) {
    const draft = await get(key);
    if (!draft || draft.synced) continue;

    // Only include drafts that explicitly belong to the current user.
    // Legacy drafts without userId are excluded (quarantined until sign-out clears them).
    if (draft.userId !== _currentUserId) continue;

    // Extract draftId from scoped key: draft_{userId}_{draftId}
    let draftId = key.replace(PREFIX, '');
    if (draftId.startsWith(`${_currentUserId}_`)) {
      draftId = draftId.substring(_currentUserId.length + 1);
    }
    unsynced.push({ draftId, ...draft });
  }
  return unsynced;
};

export const markSynced = async (draftId) => {
  const key = _scopedKey(draftId);
  const draft = await get(key);
  if (draft) {
    await set(key, { ...draft, synced: true });
  }
};

export const clearLocalDraft = async (draftId) => {
  await del(_scopedKey(draftId));
  // Also clear legacy key if it exists
  await del(`${PREFIX}${draftId}`);
};

export const clearAllLocalDrafts = async () => {
  const allKeys = await keys();
  const draftKeys = allKeys.filter(k => typeof k === 'string' && k.startsWith(PREFIX));
  for (const key of draftKeys) {
    await del(key);
  }
};
